import React, { useState, useRef, useCallback, useEffect } from "react";
import "./style.css";

import Method from "../child/method/Method";
import SingleTooth from "../child/single-tooth/SingleTooth";
import { Col, message, Row } from "antd";
import MethodReference from "../child/method-reference/MethodReference";
import PhysicalRadioGraph from "../child/physical-radio-graph/PhysicalRadioGraph";
import StageCriteria from "../child/stage-criteria/StageCriteria";
import CustomButtonWithIcon from "../../../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import CustomPrimaryButton from "../../../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import { assessmentIcons } from "../../../../utils/assessmentIcons";
import { useSelector } from "react-redux";
import {
  getExamMethodInformation,
  submitPrimaryTeethExamResult,
} from "../../../../../components/https/age-assessment-apis";
import {
  errorRequestHandel,
  ageAssessmentBasePath,
} from "../../../../../helper";
import CustomSpinner from "../../../../../dentail-indetifcation/components/d-reusable/CustomSpiner";
import axios from "axios";
import MatchStageTable from "../child/match-stage-table/MatchStageTable";
import { useNavigate } from "react-router-dom";
import Paragraph from "antd/lib/typography/Paragraph";
import { useSubscriptionGuard } from "../../../../../hooks/subscription-hook/UseSubscriptionGuard";

function PrimaryTeethStaging() {
  useSubscriptionGuard();
  const navigate = useNavigate();
  const AVAILABLE_METHODS = useSelector(
    (state) => state.ageAssessmentCase?.exameMethods || []
  );
  const currentCaseDetail = useSelector(
    (state) => state.ageAssessmentCase?.currentCaseDetail || null
  );
  let additional_images = currentCaseDetail?.additional_images || [];
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Core state
  const [methodsData, setMethodsData] = useState({}); // Cache for API data

  const [selectedMethods, setSelectedMethods] = useState([
    AVAILABLE_METHODS[0].id,
  ]); // Default to Schour Method
  const [examMethodData, setExamMethodData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStageIndex, setCurrenStageIndex] = useState(0);

  // Image controls
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRadioGraphFullscreen, setIsRadioGraphFullscreen] = useState(false);

  const [stages, setStages] = useState([]); // Current method's stages
  const [matchedStages, setMatchedStages] = useState({});

  // Refs
  const imageContainerRef = useRef(null);
  const radioGraphContainerRef = useRef(null);

  // Fetch initial method data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const defaultMethod = AVAILABLE_METHODS[0];
      if (defaultMethod) {
        await handleMethodToggle(defaultMethod.id);
      }
    };

    fetchInitialData();
  }, []);

  const fetchMethodData = async (slug) => {
    try {
      setLoading(true);
      const response = await getExamMethodInformation({ slug: slug });
      if (response.status === 200) {
        setExamMethodData(response.data.data);

        setMethodsData((prev) => ({
          ...prev,
          [slug]: response.data.data,
        }));
        setStages(response.data.data);
        setCurrenStageIndex(0);
        setLoading(false);
      }
    } catch (error) {
      errorRequestHandel({ error: error });
      setLoading(false);
    }
  };

  // Get current stage
  const currentStage = stages[currentStageIndex] || {
    id: 0,
    name: "",
    age: 0,
    deviation: 0,
    description: "Loading...",
    diagram: "/placeholder.svg?height=400&width=600&text=Loading",
    radiograph: "/placeholder.svg?height=400&width=600&text=Loading",
  };

  const handleMethodToggle = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    const isCollation = methodId === 18;
    const wasCollationSelected = selectedMethods.includes(18);
    const hasCompletedProgress = () => {
      return selectedMethods.some((id) => getMethodStatus(id) === "completed");
    };

    setSelectedMethods((prevSelected) => {
      // ✅ Case 1: User selects Collation (ID 18)
      if (isCollation) {
        const isSwitchingFromOthers =
          prevSelected.length > 0 && !prevSelected.includes(18);

        if (isSwitchingFromOthers && hasCompletedProgress()) {
          const userConfirmed = window.confirm(
            "You have completed progress in other methods. Selecting Collation will remove that. Do you want to continue?"
          );
          if (!userConfirmed) return prevSelected;
        }

        setMatchedStages({});
        return [18]; // Only Collation allowed
      }

      // ✅ Case 2: User switches from Collation to another method
      if (wasCollationSelected) {
        if (getMethodStatus(18) === "completed") {
          const userConfirmed = window.confirm(
            "You have completed progress in Collation. Switching will remove that. Do you want to continue?"
          );
          if (!userConfirmed) return prevSelected;
        }

        setMatchedStages({});
        return [methodId]; // Replace Collation with selected method
      }

      // ✅ Case 3: Regular methods - Add only, no removal
      if (!prevSelected.includes(methodId)) {
        return [...prevSelected, methodId];
      }

      return prevSelected; // Already selected, don’t remove
    });

    setSelectedMethod(method);

    // Load method data
    if (!methodsData[method.slug]) {
      await fetchMethodData(method.slug);
    } else {
      setExamMethodData(methodsData[method.slug]);
      setStages(methodsData[method.slug]); // Use cached data
      setCurrenStageIndex(0);
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (imageContainerRef.current?.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };
  const toggleRadioGrapghFullscreen = () => {
    if (!isRadioGraphFullscreen) {
      if (radioGraphContainerRef.current?.requestFullscreen) {
        radioGraphContainerRef.current.requestFullscreen();
        setIsRadioGraphFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsRadioGraphFullscreen(false);
      }
    }
  };
  const staticTeeth = [
    {
      tooth_identifier: 71,
      title: "Lower Left Central",
    },
    {
      tooth_identifier: 72,
      title: "Lower Left Lateral",
    },
    {
      tooth_identifier: 73,
      title: "Lower Left Canine",
    },
    {
      tooth_identifier: 74,
      title: "Lower left 1st Molar",
    },
    {
      tooth_identifier: 75,
      title: "Lower left 2nd Molar",
    },
  ];

  // Stage matching handler
  const handleStageMatch = (stageId) => {
    if (selectedMethod) {
      setMatchedStages((prev) => ({
        ...prev,
        [selectedMethod.id]: stageId,
      }));

      message.success("Stage matched successfully!");
    }
  };

  // Handle submission
  const handleSubmit = async () => {
    if (Object.keys(matchedStages).length === 0) {
      message.warning("Please complete atleast one method to proceed.");
      return;
    }

    const payload = {
      project_id: currentCaseDetail?.project_id,
      methods: Object.entries(matchedStages).map(([methodId, stageId]) => ({
        id: Number.parseInt(methodId),
        stage_id: stageId,
      })),
    };

    try {
      setIsLoading(true);
      const response = await submitPrimaryTeethExamResult({ payload: payload });
      if (response.status === 200) {
        message.success("Results submitted successfully!");
        navigate(`${ageAssessmentBasePath}/cases/details`, {
          state: { projectId: currentCaseDetail?.project_id },
        });
        setIsLoading(false);
      }
      // Here you might redirect or do something else on success
    } catch (error) {
      errorRequestHandel({ error: error });
      setIsLoading(false);
    }
  };

  const getMethodStatus = (methodId) => {
    if (matchedStages[methodId]) return "completed";
    if (selectedMethods?.includes(methodId)) return "selected";
    return "none";
  };

  // const isStageMatched =
  //   currentStage.id && matchedStages[selectedMethods[0]] === currentStage.id;

  const isStageMatched =
    currentStage.id && matchedStages[selectedMethod.id] === currentStage.id;

  function findStagesById(data, stageIds) {
    const result = {};

    // Iterate through each method
    for (const method in data) {
      result[method] = [];

      // Iterate through each stage in the method
      for (const stage of data[method]) {
        // Check if this stage's ID matches any of our target IDs
        if (Object.values(stageIds).includes(stage.id)) {
          result[method].push({
            id: stage.id,
            name: stage.name,
            age: stage.age,
            description: stage.description,
            diagram: stage.diagram,
            radiograph: stage.radiograph,
          });
        }
      }
      // Only add to result if there's at least one matched stage
      if (matchedStages.length > 0) {
        result[method] = matchedStages;
      }
    }

    return result;
  }

  // Find stages with IDs 8 and 12
  const foundStages = findStagesById(methodsData, matchedStages);
  // Handle delete action
  const handleDeleteMatchStageRecord = (item) => {
    // Find the key in matchedStages that corresponds to this item's ID
    const keyToRemove = Object.entries(matchedStages).find(
      ([_, value]) => value === item.id
    )?.[0];

    if (keyToRemove) {
      // Create a new object without the key to remove
      const updatedMatchedStages = { ...matchedStages };
      delete updatedMatchedStages[keyToRemove];

      // Update the matchedStages
      setMatchedStages(updatedMatchedStages);
    }
  };

  const onCancel = () => {
    if (Object.keys(matchedStages).length > 0) {
      const confirmCancel = window.confirm("Are you sure you want to cancel?");
      if (!confirmCancel) {
        return; // Stop if the user cancels
      }
    }
    window.history.back(); // Go back to the previous page
  };
  const handleClickOnTableTab = (slug) => {
    const findMethod = AVAILABLE_METHODS.find((met) => met.slug === slug);
    if (findMethod) {
      handleMethodToggle(findMethod?.id);
    }
  };

  return (
    <div className="age-estimation-interface">
      <CustomSpinner loading={loading} />
      {/* Method Selection */}
      <section className="method-selection">
        <h4>Method Selection</h4>
        <p className="method-selection-description">
          Select one or more methods for age assessment.
        </p>

        <Row gutter={[16, 16]}>
          {AVAILABLE_METHODS.map((method, index) => {
            return (
              <Col md={8} key={index}>
                <Method
                  item={method}
                  selectedMethod={selectedMethod}
                  status={getMethodStatus(method.id)}
                  onClick={() => handleMethodToggle(method.id)}
                />
              </Col>
            );
          })}
        </Row>
        <br />
        <Row
          gutter={[16, 16]}
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {staticTeeth.map((item, index) => (
            <Col
              key={index}
              style={{
                flex: "1 1 18%",
                pointerEvents: "none", // Disables clicking
                cursor: "default", // Ensures default cursor
              }}
            >
              <SingleTooth item={item} />
            </Col>
          ))}
        </Row>

        <br />
        <h2>Stage Analysis</h2>
        <Paragraph type="secondary" style={{ marginBottom: 6 }}>
          Select a tooth development stage that best matches the case
          radiograph.
        </Paragraph>
        {additional_images?.length === 0 &&
        (!currentCaseDetail?.opg || currentCaseDetail.opg.trim() == "") ? (
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <StageCriteria
                stage={currentStage}
                onStageMatched={() => handleStageMatch(currentStage.id)}
                stageMatched={isStageMatched}
              />
            </Col>
            <Col md={16}>
              <MethodReference
                onFullScreen={toggleFullscreen}
                imageContainerRef={imageContainerRef}
                data={examMethodData}
                onChangeItem={(e) => setCurrenStageIndex(e)}
                currentStageIndex={currentStageIndex}
              />
            </Col>
          </Row>
        ) : (
          <Row gutter={[16, 16]}>
            <Col md={12}>
              <MethodReference
                onFullScreen={toggleFullscreen}
                imageContainerRef={imageContainerRef}
                data={examMethodData}
                onChangeItem={(e) => setCurrenStageIndex(e)}
                currentStageIndex={currentStageIndex}
              />
              <StageCriteria
                stage={currentStage}
                onStageMatched={() => handleStageMatch(currentStage.id)}
                stageMatched={isStageMatched}
              />
            </Col>

            <Col md={12}>
              <PhysicalRadioGraph
                onFullScreen={toggleRadioGrapghFullscreen}
                radioGraphContainerRef={radioGraphContainerRef}
              />
            </Col>
          </Row>
        )}

        <br />
        <MatchStageTable
          foundStages={foundStages}
          onDelete={handleDeleteMatchStageRecord}
          onTabClick={handleClickOnTableTab}
        />
        <Row
          gutter={[16, 16]}
          justify={"space-between"}
          style={{ padding: "0 10px" }}
        >
          <CustomButtonWithIcon
            title="Cancel"
            icon={assessmentIcons.cancel}
            color={"#DC1919"}
            onClick={onCancel}
          />
          <CustomPrimaryButton
            title="Proceed"
            icon={assessmentIcons.proceed}
            onClick={handleSubmit}
            disabled={Object.keys(matchedStages).length === 0 || isLoading}
            isLoading={isLoading}
          />
        </Row>
      </section>
    </div>
  );
}

export default PrimaryTeethStaging;
